import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CompanySearchField from "@/components/CompanySearchField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Plus, Search, Car, Phone, Mail, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { client_type: "particulier", first_name: "", last_name: "", company: "", siren: "", siret: "", address: "", phone: "", email: "", acquisition_source: "", notes: "" };
const SOURCES = [["", "—"], ["google", "Google"], ["google_maps", "Google Maps"], ["facebook", "Facebook"], ["instagram", "Instagram"], ["tiktok", "TikTok"], ["bouche_a_oreille", "Bouche-à-oreille"], ["ancien_client", "Ancien client"], ["google_ads", "Google Ads"], ["meta_ads", "Publicité Meta"], ["site_internet", "Site internet"], ["passage", "Passage devant le local"], ["partenaire", "Partenaire"], ["autre", "Autre"]];

export default function Clients() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: q ? { search: q } : {} });
      setClients(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("new")) { setOpen(true); setParams({}); }
  }, [params, setParams]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const save = async () => {
    const isPro = form.client_type === "professionnel";
    if (isPro && !form.company) return toast.error("Le nom de la société est requis");
    if (!isPro && !form.last_name) return toast.error("Le nom est requis");
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client créé");
      setOpen(false);
      setForm(EMPTY);
      load(search);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={`${clients.length} client${clients.length > 1 ? "s" : ""}`}
        icon={Users}
        actions={
          <Button onClick={() => setOpen(true)} data-testid="btn-new-client" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau client
          </Button>
        }
      />

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (nom, téléphone, email, immatriculation)"
          data-testid="input-search-clients"
          className="pl-9 bg-white border-slate-200"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
          Aucun client. Cliquez sur « Nouveau client » pour commencer.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/clients/${c.id}`)}
              data-testid={`client-card-${c.id}`}
              className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-500/40 hover:bg-emerald-500/[0.03] transition-all"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center font-semibold text-sm">
                  {((c.first_name?.[0] || "") + (c.last_name?.[0] || "")) || (c.company?.[0] || "?")}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company}</div>
                  {c.company && `${c.first_name || ""}${c.last_name || ""}` && <div className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" />{c.company}</div>}
                </div>
              </div>
              <div className="space-y-1 text-xs text-slate-500">
                {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone}</div>}
                {c.email && <div className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" />{c.email}</div>}
                <div className="flex items-center gap-1.5"><Car className="w-3 h-3" />{c.vehicle_count} véhicule{c.vehicle_count > 1 ? "s" : ""}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">Nouveau client</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex gap-2 mb-1">
              {[["particulier", "Particulier"], ["professionnel", "Professionnel"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, client_type: k })} data-testid={`client-type-${k}`}
                  className={`flex-1 py-2 rounded-lg border text-sm transition ${form.client_type === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-slate-50 border-slate-200 text-slate-500"}`}>{l}</button>
              ))}
            </div>
            {form.client_type === "professionnel" && (
              <CompanySearchField value={form} onChange={(patch) => setForm({ ...form, ...patch })} testidPrefix="client-company" />
            )}
            <Field label={form.client_type === "professionnel" ? "Prénom du contact" : "Prénom"}><Input data-testid="input-client-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-slate-50 border-slate-200" /></Field>
            <Field label={form.client_type === "professionnel" ? "Nom du contact" : "Nom *"}><Input data-testid="input-client-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-slate-50 border-slate-200" /></Field>
            <Field label="Téléphone"><Input data-testid="input-client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-50 border-slate-200" /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-50 border-slate-200" /></Field>
            <Field label="Comment nous a-t-il connu ?"><select value={form.acquisition_source || ""} onChange={(e) => setForm({ ...form, acquisition_source: e.target.value })} data-testid="client-acquisition-source" className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm">{SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
            <Field label="Adresse" full><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-slate-50 border-slate-200" /></Field>
            <Field label="Notes" full><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-50 border-slate-200" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-client" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
